import { Component, Input, OnInit } from '@angular/core';
import { SportRow } from "@shared/interfaces/sports.interface";
import {
  ToggleChangeEvent,
  KeyPressEvent,
  PasteEvent,
  InputChangeEvent
} from '../sports-table/sports-table.component';
import { MaximumAerobicPanelComponentState, StateStatus } from './maximum-aerobic-panel.component.state';
import { Observable } from 'rxjs';
import { SportDto } from '@core/services/sports/sport-dto';
import { IMaximumAerobicSpeedPowerSetting } from '@shared/interfaces/metric-settings.interface';
import { AppConstants } from '@core/constants';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';

@Component({
  selector: 'app-maximum-aerobic-panel',
  templateUrl: './maximum-aerobic-panel.component.html',
  styleUrls: ['./maximum-aerobic-panel.component.scss'],
  providers: [MaximumAerobicPanelComponentState],
})
export class MaximumAerobicPanelComponent implements OnInit {
  @Input() featureId: number;
  @Input() featureEnabled = false;
  @Input() heading: string = '';

  public loading$: Observable<boolean>;
  public saving$: Observable<boolean>;
  public settings$: Observable<IMaximumAerobicSpeedPowerSetting[] | null>;
  public stateStatus$: Observable<StateStatus | null>;
  public sports$: Observable<SportDto[]>;

  public sportRows: SportRow[] = [];

  constructor(
    private snackBar: MatSnackBar,
    private readonly state: MaximumAerobicPanelComponentState,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.linkState();
    if (this.featureEnabled) {
      this.state.loadState(this.featureId);
      this.setupListeners();
    }
  }

  private populateForm(settings?: IMaximumAerobicSpeedPowerSetting[]): void {
    this.sports$.subscribe(sports => {
      if (sports == null || !Array.isArray(sports) || sports.length == 0) {
        return
      }

      const settingsMap = (settings ?? []).reduce((acc, setting) => {
        acc[setting.slug] = setting;
        return acc;
      }, {} as Record<string,IMaximumAerobicSpeedPowerSetting>);

      this.sportRows = sports.map(sport => ({
        name: sport.name!,
        slug: sport.feature_slug!,
        hasToggle: sport.simulation_type === 1,
        isDuration: (sport.simulation_type === 2 || settingsMap[sport.feature_slug!]?.isDuration) ?? true,
        value: settingsMap[sport.feature_slug!]?.value ?? '04:00',
        error: ''
      }));
    });
  }

  public onToggleChange(toggleEvent: ToggleChangeEvent): void {
    const { event, sport } = toggleEvent;
    sport.isDuration = event.checked;
    sport.value = '';
    sport.error = '';
  }

  public onKeyPress(keyEvent: KeyPressEvent): boolean {
    const { event, sport } = keyEvent;
    const char = String.fromCharCode(event.which);
    const currentValue = (event.target as HTMLInputElement).value;

    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true) ||
      (event.keyCode === 90 && event.ctrlKey === true)) {
      return true;
    }

    if (sport.hasToggle && !sport.isDuration) {
      if (!/[0-9]/.test(char)) {
        event.preventDefault();
        return false;
      }
    } else {
      if (!/[0-9:.,]/.test(char)) {
        event.preventDefault();
        return false;
      }

      if (char === ':') {
        if (currentValue.includes(':') || currentValue.length === 0) {
          event.preventDefault();
          return false;
        }
      }

      if (char === '.' || char === ',') {
        const hasDot = currentValue.includes('.');
        const hasComma = currentValue.includes(',');

        if (hasDot || hasComma || !currentValue.includes(':')) {
          event.preventDefault();
          return false;
        }

        const dotIndex = currentValue.indexOf('.');
        const commaIndex = currentValue.indexOf(',');
        const separatorIndex = Math.max(dotIndex, commaIndex);

        if (separatorIndex !== -1 && currentValue.length > separatorIndex + 1) {
          event.preventDefault();
          return false;
        }
      }

      const dotIndex = currentValue.indexOf('.');
      const commaIndex = currentValue.indexOf(',');
      const separatorIndex = Math.max(dotIndex, commaIndex);

      if (separatorIndex !== -1) {
        const charsAfterSeparator = currentValue.length - separatorIndex - 1;
        if (charsAfterSeparator >= 1 && !/[0-9]/.test(char)) {
          event.preventDefault();
          return false;
        }

        if (charsAfterSeparator >= 1 && /[0-9]/.test(char)) {
          event.preventDefault();
          return false;
        }
      }

      const newValue = currentValue + char;
      if (newValue.length > 7) {
        event.preventDefault();
        return false;
      }
    }

    return true;
  }

  public onPaste(pasteEvent: PasteEvent): void {
    const { event, sport } = pasteEvent;
    event.preventDefault();
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData?.getData('text') || '';

    if (this.isValidInput(pastedText, sport)) {
      sport.value = pastedText;
      this.validateInput(sport);
    }
  }

  public onInputChange(inputEvent: InputChangeEvent): void {
    const { event, sport } = inputEvent;
    const target = event.target as HTMLInputElement;
    const value = target.value;

    let filteredValue = '';

    if (sport.hasToggle && !sport.isDuration) {
      filteredValue = value.replace(/[^0-9]/g, '');
    } else {
      const parts = value.split(':');
      if (parts.length <= 2) {
        filteredValue = value.replace(/[^0-9:.,]/g, '');
        const colonCount = (filteredValue.match(/:/g) || []).length;
        const dotCount = (filteredValue.match(/\./g) || []).length;
        const commaCount = (filteredValue.match(/,/g) || []).length;

        if (colonCount > 1) {
          const firstColonIndex = filteredValue.indexOf(':');
          filteredValue = filteredValue.substring(0, firstColonIndex + 1) +
            filteredValue.substring(firstColonIndex + 1).replace(/:/g, '');
        }

        if (dotCount > 0 && commaCount > 0) {
          const dotIndex = filteredValue.indexOf('.');
          const commaIndex = filteredValue.indexOf(',');
          if (dotIndex < commaIndex) {
            filteredValue = filteredValue.replace(/,/g, '');
          } else {
            filteredValue = filteredValue.replace(/\./g, '');
          }
        }

        if (dotCount > 1) {
          const firstDotIndex = filteredValue.indexOf('.');
          filteredValue = filteredValue.substring(0, firstDotIndex + 1) +
            filteredValue.substring(firstDotIndex + 1).replace(/\./g, '');
        }

        if (commaCount > 1) {
          const firstCommaIndex = filteredValue.indexOf(',');
          filteredValue = filteredValue.substring(0, firstCommaIndex + 1) +
            filteredValue.substring(firstCommaIndex + 1).replace(/,/g, '');
        }

        const dotIndex = filteredValue.indexOf('.');
        const commaIndex = filteredValue.indexOf(',');
        const separatorIndex = Math.max(dotIndex, commaIndex);

        if (separatorIndex !== -1) {
          if (filteredValue.length > separatorIndex + 2) {
            filteredValue = filteredValue.substring(0, separatorIndex + 2);
          }

          const afterSeparator = filteredValue.substring(separatorIndex + 1);
          const cleanedAfterSeparator = afterSeparator.replace(/[^0-9]/g, '');
          filteredValue = filteredValue.substring(0, separatorIndex + 1) + cleanedAfterSeparator;
        }
      } else {
        filteredValue = parts[0] + ':' + parts[1];
      }
    }

    if (filteredValue !== value) {
      sport.value = filteredValue;
    }

    this.validateInput(sport);
  }

  tryForFreeText(): string {
    return "Activate";
  }

  public purchaseMetric(): void {
    const feature_slug = 'max_aerobic';
    this.router.navigate(['home/feature-store', 'feature-detail'], {
      queryParams: {
        feature_slug,
        tab_index_to_return: 0
      }
    });
  }

  private isValidInput(value: string, sport: SportRow): boolean {
    if (sport.hasToggle && !sport.isDuration) {
      return /^\d*$/.test(value);
    } else {
      return /^(\d{1,2}):(\d{2})[.,](\d{1})$/.test(value) ||
        /^(\d{1,2}):(\d{2})$/.test(value) ||
        /^\d{1,2}$/.test(value);
    }
  }

  private validateInput(sport: SportRow): void {
    sport.error = '';

    if (!sport.value.trim()) {
      return;
    }

    if (sport.hasToggle && !sport.isDuration) {
      if (!/^\d+$/.test(sport.value)) {
        sport.error = 'Please enter a valid number for meters';
      } else if (parseInt(sport.value) <= 0) {
        sport.error = 'Value must be greater than 0';
      }
    } else {
      const timePatternWithMs = /^(\d{1,2}):(\d{2})[.,](\d{1})$/;
      const timePattern = /^(\d{1,2}):(\d{2})$/;
      const msMatch = sport.value.match(timePatternWithMs);
      const match = sport.value.match(timePattern);

      if (!msMatch && !match && !/^\d{1,2}$/.test(sport.value)) {
        sport.error = 'Please enter time in mm:ss.d, mm:ss, or mm format (e.g., 15:59.5, 15:59, or 15)';
      } else if (msMatch) {
        const minutes = parseInt(msMatch[1]);
        const seconds = parseInt(msMatch[2]);

        if (seconds >= 60) {
          sport.error = 'Seconds must be less than 60';
        } else if (minutes < 0 || seconds < 0) {
          sport.error = 'Time values must be positive';
        }
      } else if (match) {
        const minutes = parseInt(match[1]);
        const seconds = parseInt(match[2]);

        if (seconds >= 60) {
          sport.error = 'Seconds must be less than 60';
        } else if (minutes < 0 || seconds < 0) {
          sport.error = 'Time values must be positive';
        }
      }
    }
  }

  public onSave(): void {
    this.sportRows.forEach(sport => this.validateInput(sport));

    if (this.hasValidationErrors()) {
      return;
    }

    const settingsData: IMaximumAerobicSpeedPowerSetting[] = this.sportRows.map(sportRow => ({
      slug: sportRow.slug,
      isDuration: sportRow.isDuration,
      value: sportRow.value
    }));

    this.state.saveSettings(this.featureId, settingsData);
  }

  public hasValidationErrors(): boolean {
    return this.sportRows.some(sport => sport.error && sport.error.trim() !== '');
  }

  public getValidationErrorMessage(): string {
    const errorsCount = this.sportRows.filter(sport => sport.error && sport.error.trim() !== '').length;
    if (errorsCount === 1) {
      return 'Please fix the validation error before saving.';
    }
    return `Please fix ${errorsCount} validation errors before saving.`;
  }

  private linkState(): void {
    this.loading$ = this.state.loading$;
    this.saving$ = this.state.saving$;
    this.settings$ = this.state.settings$;
    this.stateStatus$ = this.state.status$;
    this.sports$ = this.state.sports$;
  }

  private setupListeners(): void {
    this.settingsListener();
    this.sportsListener();
    this.statusListener();
  }

  private settingsListener(): void {
    this.settings$.subscribe(settings => {
      if (!settings) {
        return
      }

      this.populateForm(settings);
    })
  }

  private sportsListener(): void {
    this.sports$.subscribe(sports => {
      if (sports == null || !Array.isArray(sports) || sports.length === 0) {
        return
      }

      this.populateForm();
    })
  }

  private statusListener(): void {
    this.stateStatus$.subscribe(status => {
      if (!status) {
        return
      }

      switch (status.status) {
        case 'ok':
          this.snackBar.open(status.message, 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
          break
        case 'warning':
          this.snackBar.open(status.message, 'Warning', AppConstants.TOAST_CONFIG.WARNING);
          break
        case 'error':
          this.snackBar.open(status.message, 'Error', AppConstants.TOAST_CONFIG.ERROR);
          break
        default:
          break
      }
    })
  }
}
